import classNames from 'classnames';
import React, { forwardRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Else, If, Then, When } from 'react-if';

import { IconHidePassword, IconShowPassword } from '../icons';
import { InputFieldProps, InputFieldSize } from './input-field.types';

const sizeClasses: Record<InputFieldSize, string> = {
  small: 'input-field-sm',
  medium: 'input-field-md',
  large: 'input-field-lg',
};

export const InputField: React.FC<InputFieldProps> = forwardRef<
  HTMLInputElement,
  InputFieldProps
>(
  (
    {
      id,
      className,
      labelKey,
      type = 'text',
      size = 'medium',
      error,
      RightIcon,
      LeftIcon,
      helperTextKey,
      helperTextValue,
      placeholder,
      ...rest
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [showPassword, setShowPassword] = useState(false);
    const hasError = !!error;
    const containerClasses = classNames(
      'input-field-wrapper',
      className,
      {
        [sizeClasses[size]]: size,
      },
      { 'has-error': hasError }
    );

    const labelClasses = classNames('input-field-label', {
      'left-icon': LeftIcon,
    });

    const inputId = id ?? rest.name;

    const textType = type === 'password' && showPassword ? 'text' : type;

    const toggleShowPassword = () => setShowPassword(showPass => !showPass);

    return (
      <div className={containerClasses}>
        <div className="input-field-content">
          {LeftIcon && <LeftIcon className="input-field-icon" />}
          <input
            id={inputId}
            ref={ref}
            type={textType}
            className="input-field"
            placeholder={placeholder ?? ' '}
            {...rest}
          />
          <If condition={RightIcon !== undefined}>
            <Then>
              {RightIcon && (
                <RightIcon className="input-field-icon input-field-icon-right" />
              )}
            </Then>
            <Else>
              <When condition={type === 'password'}>
                <If condition={!showPassword}>
                  <Then>
                    <IconHidePassword
                      className="input-field-icon input-field-icon-right input-field-icon-clickable"
                      onClick={toggleShowPassword}
                    />
                  </Then>
                  <Else>
                    <IconShowPassword
                      className="input-field-icon input-field-icon-right input-field-icon-clickable"
                      onClick={toggleShowPassword}
                    />
                  </Else>
                </If>
              </When>
            </Else>
          </If>
          <When condition={labelKey !== undefined}>
            <label htmlFor={inputId} className={labelClasses}>
              <Trans t={t}>{labelKey}</Trans>
            </label>
          </When>
        </div>
        <When condition={helperTextKey !== undefined}>
          <p className="input-field-helper-text">
            <If condition={hasError}>
              <Then>
                <Trans t={t} values={helperTextValue}>
                  {error}
                </Trans>
              </Then>
              <Else>
                <Trans t={t} values={helperTextValue}>
                  {helperTextKey}
                </Trans>
              </Else>
            </If>
          </p>
        </When>
      </div>
    );
  }
);

InputField.displayName = 'InputField';

export default InputField;
